export const UNIT = 'empaque';
export const YEAR_START = '2026-01-01';
export const YEAR_END = '2026-12-31';
export const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
export const money = value => value == null ? 'No disponible' : Number(value).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
export function cents(value) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value ?? ''));
    if (!match) throw new Error('Importe contable inválido.');
    const fraction = (match[3] || '').padEnd(3, '0');
    const amount = Number(match[2]) * 100 + Number(fraction.slice(0, 2)) + (Number(fraction[2]) >= 5 ? 1 : 0);
    if (!Number.isSafeInteger(amount)) throw new Error('Importe fuera del límite seguro.');
    return match[1] ? -amount : amount;
}
export function inRange(date, from, to) { const day = String(date || '').slice(0, 10); return day >= from && day <= to; }
export function validRange(from, to) {
    return [from, to].every(value => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value) && from <= to && from >= YEAR_START && to <= YEAR_END;
}
export async function pages(api, path, size = 500) {
    const rows = [], seen = new Set();
    for (let offset = 0; offset <= 100000; offset += size) {
        const page = await api(`${path}&offset=${offset}`);
        if (!Array.isArray(page)) throw new Error('Respuesta de catálogo inválida.');
        for (const row of page) {
            if (!row._id || seen.has(String(row._id))) throw new Error('El catálogo cambió durante la consulta; vuelve a cargar.');
            seen.add(String(row._id)); rows.push(row);
        }
        if (page.length < size) return rows;
    }
    throw new Error('Catálogo incompleto: límite de consulta alcanzado.');
}
const types = ['items', 'item', 'accounts', 'account', 'vendors', 'vendor', 'bills', 'bill', 'purchases', 'purchase'];
const keyOf = (realm, id) => JSON.stringify([String(realm), String(id)]);
const entityKey = row => keyOf(row.realmId, `${row.entityType.replace(/s$/, '')}:${row.quickBooksId}`);
const opening = number => /^(inv inicial|inventario inicial|saldo inicial|opening balance|initial inventory)$/.test(normalize(number));
function signature(summary) {
    return JSON.stringify((summary.counts || []).filter(row => types.includes(row._id?.entityType)).map(row => [row._id?.businessUnit, row._id?.entityType, row.count, row.lastSyncedAt]).sort());
}
export async function readEntities(readSynced, type, expected) {
    if (!Number.isSafeInteger(expected) || expected < 0 || expected > 20000) throw new Error(`Conteo de ${type} fuera del límite seguro.`);
    if (!expected) return [];
    const rows = [], seen = new Set();
    let requests = 0;
    async function read(pattern = null) {
        if (++requests > 500) throw new Error('La lectura contable requiere demasiadas particiones.');
        const result = await readSynced(`entities?businessUnit=${UNIT}&entityType=${type}&limit=500${pattern ? `&search=${encodeURIComponent(pattern)}` : ''}`);
        if (!Array.isArray(result) || result.length > 500 || result.some(row => row.businessUnit !== UNIT || row.entityType !== type || pattern && !new RegExp(pattern, 'i').test(row.displayName || ''))) throw new Error('Fuente contable fuera de alcance.');
        return result;
    }
    function append(values) {
        for (const row of values) {
            if (!row._id || seen.has(String(row._id))) throw new Error('Particiones contables repetidas o inconsistentes.');
            seen.add(String(row._id)); rows.push(row);
        }
    }
    async function partition(prefix, depth) {
        if (depth > 12) throw new Error('No se puede demostrar que la fuente esté completa.');
        const exact = await read(`^${prefix}$`);
        if (exact.length === 500) throw new Error('Demasiadas facturas con el mismo identificador visible.');
        append(exact);
        for (const group of ['[0-4]', '[5-9]', '[a-m]', '[n-z]', '[^a-z0-9]']) {
            const branch = prefix + group, result = await read(`^${branch}`);
            if (result.length === 500) await partition(branch, depth + 1);
            else append(result);
        }
    }
    if (expected <= 500) append(await read());
    else await partition('', 0);
    if (rows.length !== expected) throw new Error('La fuente contable está incompleta; actualiza los datos.');
    return rows;
}
export async function loadCosts(api, readSynced) {
    const units = await api('business-units');
    if (!Array.isArray(units) || !units.includes(UNIT)) throw new Error('Sin acceso a Amevel Empaques.');
    const [products, suppliers, summary] = await Promise.all([
        pages(api, `products?businessUnit=${UNIT}&limit=500`),
        pages(api, `suppliers?businessUnit=${UNIT}&limit=500`),
        readSynced(`sync/summary?businessUnit=${UNIT}`)
    ]);
    if (products.some(product => product.businessUnit !== UNIT) || suppliers.some(supplier => !supplier.businessUnits?.includes(UNIT))) throw new Error('Se descartaron datos fuera de Amevel Empaques.');
    if (summary.businessUnit !== UNIT || !Array.isArray(summary.counts)) throw new Error('Resumen contable fuera de alcance.');
    const entities = [];
    for (const type of types) {
        const counts = summary.counts.filter(row => row._id?.entityType === type);
        if (counts.some(row => row._id?.businessUnit !== UNIT)) throw new Error('Conteos fuera de Amevel Empaques.');
        const expected = counts.reduce((sum, row) => sum + row.count, 0);
        const rows = await readEntities(readSynced, type, expected);
        entities.push(...rows);
    }
    const after = await readSynced(`sync/summary?businessUnit=${UNIT}`);
    if (after.businessUnit !== UNIT || signature(summary) !== signature(after)) throw new Error('QuickBooks se sincronizó durante la lectura; actualiza para obtener un corte consistente.');
    return buildCosts({products, suppliers, entities});
}
function uniqueLinks(records, supplier = false) {
    const result = new Map();
    for (const record of records) for (const link of record.qbLinks || []) {
        if (supplier && link.businessUnit !== UNIT) continue;
        const key = keyOf(link.realmId, link.quickBooksId);
        if (result.has(key) && result.get(key)?._id !== record._id) throw new Error('Hay correspondencias contables ambiguas; revisa los vínculos existentes.');
        result.set(key, record);
    }
    return result;
}
function currencyOf(document, supplier) {
    const current = document.raw?.CurrencyRef?.value || document.currency;
    if (current) return /^[A-Z]{3}$/.test(current) ? current : null;
    return supplier?.defaultCurrency === 'USD' && supplier.history?.some(row => row.action === 'currency_confirmed' && row.detail?.currency === 'USD') ? 'USD' : null;
}
function quantityUnit(product, realmId, itemId) {
    if (product.qbSource?.realmId !== realmId || String(product.qbSource?.quickBooksId) !== String(itemId)) return null;
    if (product.qbSource.unit) return product.qbSource.unit;
    return product.history?.some(row => row.action === 'imported_from_synced_quickbooks') && !product.history.some(row => row.action === 'updated') ? product.baseUnit : null;
}
export function categoryName(product, line) {
    const source = product?.name || line.product || '';
    const leaf = normalize(source.split(':').pop());
    if (/\bvasos?\b/.test(leaf)) return 'Vasos';
    if (/\btapas?\b/.test(leaf)) return 'Tapas';
    return product?.category || (source.split(':').length > 2 ? source.split(':')[1] : 'Sin subcategoría');
}
export function productName(product, line) {
    const name = String(product?.name || line.product || line.description || '').split(':').pop();
    const code = String(product?.code || line.code || '');
    const plain = (code && name.startsWith(code) ? name.slice(code.length) : name).trim();
    return plain.split(/\s+(?=\d)|\s+(?:BEBIDA|PERSONALIZADO|GENERICA|GENÉRICA)\b/i)[0].trim() || plain || 'Nombre no disponible';
}
function excluded(description, account) {
    return /entrega.*(?:cliente|cedi|grupo rey)|cedi rey|muestras|samples|demostracion/.test(normalize(`${description} ${account}`));
}
export function buildCosts({products, suppliers, entities}) {
    const canonical = new Map();
    const updated = row => new Date(row.quickBooksUpdatedAt || row.raw?.MetaData?.LastUpdatedTime || row.updatedAt || 0).getTime();
    for (const entity of entities) {
        if (entity.businessUnit !== UNIT) throw new Error('Fuente fuera de Amevel Empaques.');
        const key = entityKey(entity), previous = canonical.get(key);
        if (!previous || updated(entity) > updated(previous) || updated(entity) === updated(previous) && entity.entityType.endsWith('s')) canonical.set(key, entity);
    }
    const sources = [...canonical.values()];
    const items = new Map(sources.filter(row => ['item', 'items'].includes(row.entityType)).map(row => [keyOf(row.realmId, row.quickBooksId), row]));
    const accounts = new Map(sources.filter(row => ['account', 'accounts'].includes(row.entityType)).map(row => [keyOf(row.realmId, row.quickBooksId), row]));
    const vendors = new Map(sources.filter(row => ['vendor', 'vendors'].includes(row.entityType)).map(row => [keyOf(row.realmId, row.quickBooksId), row]));
    const productLinks = uniqueLinks(products), supplierLinks = uniqueLinks(suppliers, true);
    const rows = [], invoices = new Map();
    for (const document of sources.filter(row => ['bill', 'bills', 'purchase', 'purchases'].includes(row.entityType))) {
        const raw = document.raw || {}, number = document.docNumber || raw.DocNumber || document.quickBooksId;
        const date = String(raw.TxnDate || document.txnDate || '').slice(0, 10);
        if (!inRange(date, YEAR_START, YEAR_END) || opening(number)) continue;
        const vendor = raw.VendorRef || (raw.EntityRef?.type === 'Vendor' ? raw.EntityRef : null);
        const vendorKey = keyOf(document.realmId, vendor?.value || `unknown:${document.quickBooksId}`);
        const registered = vendor && supplierLinks.get(vendorKey), vendorEntity = vendors.get(vendorKey);
        const supplierRecord = registered || {_id: `qb:${vendorKey}`, name: vendorEntity?.displayName || vendorEntity?.raw?.DisplayName || vendor?.name || 'Proveedor sin identificar', country: vendorEntity?.raw?.BillAddr?.Country || '', qbLinks: []};
        const currency = currencyOf(document, registered);
        const documentKey = entityKey(document), charges = [], seen = new Set();
        invoices.set(documentKey, {key: documentKey, date, number, quickBooksId: document.quickBooksId, realmId: document.realmId, supplierRecord, currency, charges});
        for (const [index, line] of (raw.Line || []).entries()) {
            const detail = line.ItemBasedExpenseLineDetail, ref = detail?.ItemRef;
            const item = ref && items.get(keyOf(document.realmId, ref.value));
            const accountRef = line.AccountBasedExpenseLineDetail?.AccountRef || item?.raw?.ExpenseAccountRef;
            const account = accounts.get(keyOf(document.realmId, accountRef?.value));
            const accountName = account?.raw?.FullyQualifiedName || account?.displayName || account?.raw?.Name || accountRef?.name || '';
            const description = String(line.Description || item?.raw?.Name || ref?.name || '');
            const lineId = String(line.Id ?? index), key = `${documentKey}:${lineId}`;
            if (seen.has(lineId)) throw new Error('Partidas contables duplicadas en una factura.');
            seen.add(lineId);
            if (excluded(description, accountName)) continue;
            const logistics = /\b(arancel(?:es)?|duty|duties|seguro|insurance|brokerage|aduana|customs|descarga|unloading|flete|freight|shipping|transporte|acarreos?|envios?)\b/.test(normalize(`${description} ${accountName}`));
            const goods = item && ['Inventory', 'NonInventory'].includes(item.raw?.Type) && (item.raw.Type === 'Inventory' || !logistics);
            if (goods) {
                const productRecord = productLinks.get(keyOf(document.realmId, ref.value)) || {_id: `qb:${keyOf(document.realmId, ref.value)}`, name: item.raw?.FullyQualifiedName || item.displayName || item.raw?.Name || ref.name, code: item.raw?.Sku || `QB-${ref.value}`, qbSource: {purchaseDescription: item.raw?.PurchaseDesc, salesDescription: item.raw?.Description}};
                const row = {key, itemKey: `${supplierRecord._id}:${productRecord._id}`, documentKey, productRecord, supplierRecord, product: productRecord.name, code: productRecord.code, date, number, quickBooksId: document.quickBooksId, lineId, description, currency, unitPrice: detail.UnitPrice ?? null, lineAmount: line.Amount ?? null, quantity: detail.Qty ?? null, unit: quantityUnit(productRecord, document.realmId, ref.value), adjustment: raw.Credit === true || raw.Credit === 'true' || Number(line.Amount) < 0 || Number(detail.Qty) < 0};
                rows.push({...row, name: productName(productRecord, row), category: categoryName(productRecord, row)});
            } else if (!item || item.raw?.Type !== 'Inventory') {
                if (/\bflete\b/.test(normalize(description)) && /^costos empaques(?::|\s*-)/.test(normalize(accountName))) {
                    charges.push({key, date, invoice: number, record: document.quickBooksId, description, currency, originalCents: line.Amount == null ? null : cents(line.Amount), fobCents: line.Amount == null ? null : cents(line.Amount) * (/clearing/i.test(accountName) ? -1 : 1)});
                }
            }
        }
    }
    rows.sort((first, second) => second.date.localeCompare(first.date) || first.key.localeCompare(second.key));
    return {rows, invoices, loadedAt: new Date().toISOString()};
}
export function filteredCosts(data, {from, to, category = '', supplier = '', search = '', selection = null}) {
    const range = validRange(from, to) ? data.rows.filter(row => inRange(row.date, from, to)) : [];
    const categories = [...new Set(data.rows.map(row => row.category))].sort();
    const matching = range.filter(row => (!category || row.category === category) && normalize(`${row.name} ${row.code} ${row.product} ${row.category} ${row.supplierRecord.name}`).includes(normalize(search)));
    const suppliers = [...new Map(matching.map(row => [String(row.supplierRecord._id), row.supplierRecord])).values()];
    const scoped = matching.filter(row => !supplier || String(row.supplierRecord._id) === supplier);
    const candidates = [...new Map(scoped.map(row => [row.itemKey, row])).values()];
    const selected = new Set(selection || candidates.map(row => row.itemKey));
    return {categories, suppliers, candidates, visible: scoped.filter(row => selected.has(row.itemKey)), selectedCount: candidates.filter(row => selected.has(row.itemKey)).length};
}
export function directoryRows(data, visible) {
    const grouped = new Map();
    for (const line of visible) {
        const id = String(line.supplierRecord._id);
        if (!grouped.has(id)) grouped.set(id, {supplier: line.supplierRecord, invoices: new Map(), totalCents: 0});
        const row = grouped.get(id);
        const amount = line.lineAmount == null ? null : cents(line.lineAmount);
        row.totalCents = row.totalCents == null || line.currency !== 'USD' || amount == null ? null : row.totalCents + (line.adjustment && amount > 0 ? -amount : amount);
        const invoice = data.invoices.get(line.documentKey);
        if (invoice) row.invoices.set(invoice.key, invoice);
    }
    return [...grouped.values()].map(row => {
        const invoices = [...row.invoices.values()], charges = invoices.flatMap(invoice => invoice.charges);
        const fobKnown = invoices.every(invoice => invoice.charges.length && invoice.charges.every(charge => charge.currency === 'USD' && charge.fobCents != null));
        return {...row, invoices, charges, fobCents: fobKnown ? charges.reduce((sum, charge) => sum + charge.fobCents, 0) : null};
    }).sort((first, second) => first.supplier.name.localeCompare(second.supplier.name));
}
